// ============================================================
// Create Ad Sales — Mock data & types
// Source: design-bundle AdSalesWizard.jsx + SBData.jsx
// ============================================================

import moment from 'moment';
import type { ApiInvoicingUnit } from '../../services/campaignManager/types';

// Re-export shared constants from Upsize mockData
export { COUNTRY_TZ } from '../CreateUpsize/domain';

// Ad-Sales-specific merchant with full legalEntity + bankAccount data for invoicing auto-fill
// Empty merchant fallback — replaces mock "Expedia" data. Shows blanks instead of fake data.
export const AD_SALES_MERCHANT = {
  id: '',
  merchantId: '',
  name: '',
  country: '',
  countryName: '',
  initial: '',
  brands: [] as Array<{
    id: string;
    name: string;
    category: string;
    legalEntity?: Record<string, unknown>;
    bankAccount?: Record<string, unknown>;
  }>,
  campaigns: [] as Array<{ id: string; name: string; start: string; end: string }>,
};

// Keep MOCK_MERCHANT pointing to the same empty fallback
export const MOCK_MERCHANT = AD_SALES_MERCHANT;

// --- Types ---

export interface AdSalesAsset {
  id: string;
  name: string;
  /** AUTO = system-rendered by CP Next, MANUAL = BD-configured, CRM = push/popup, EDM = email */
  group: 'AUTO' | 'MANUAL' | 'CRM' | 'EDM';
  /** Display duration — e.g. "× 30 days", "× 5 sends" */
  duration?: string;
  /** Numeric quantity (days or send count) */
  qty?: number;
  /** Rendering mode — Auto assets get a Preview button */
  mode?: 'Auto' | 'Manual';
  /** Asset timeType from the backend (e.g. "START_ONLY", "START_END") */
  timeType?: string;
  /** Asset type from the backend (e.g. "DISPLAY") */
  type?: string;
  /** Whether the asset is automated (system-rendered) */
  isAutomated?: boolean;
  /** Canonical sales asset ID — references mic_sales_asset on the backend */
  salesAssetId?: string;
}

export interface AdSalesSurface {
  label: string;
  assets: AdSalesAsset[];
}

export interface AdSalesPackage {
  id: string;
  name: string;
  description: string;
  /** Price in merchant's local currency */
  priceSGD: number;
  priceAUD?: number;
  /** Currency code from API (e.g. SGD, AUD, MYR) */
  currency?: string;
  /** Media value — what the package is "worth" vs its price */
  valueSGD?: number;
  valueAUD?: number;
  /** Duration in calendar days */
  durationDays: number;
  /** Human-readable duration */
  durationLabel: string;
  /** Structured surface/asset list */
  surfaces: AdSalesSurface[];
  /** Availability */
  slotsAvailable: number;
  soldOut?: boolean;
  /** Badge key — 'Best value', 'Recommended', 'Premium' */
  topBadge?: string;
}

export interface AdSalesInstance {
  instanceId: string;
  packageId: string;
  /**
   * Server-side DB row ID for this package (e.g. "100085835").
   * Present on edit/resume flows; absent for freshly-created instances.
   * Must be included in PATCH payloads so the backend updates rather than creates.
   */
  serverPackageId?: string;
  packageName: string;
  packagePrice: number;
  durationDays: number;
  durationLabel: string;
  startDate: Date | null;
  endDate: Date | null;
  acknowledgedSoldOut: boolean;
  /** Per-asset scheduling: { [assetName]: Date[] } */
  assetSchedule?: Record<string, Date[]>;
  /**
   * Flat asset list for this package instance — carried here so the wizard
   * mapper can build `assets[]` without needing the full package catalog.
   * Populated from `flattenAssets(pkg)` when an instance is first created.
   */
  assets?: AdSalesAsset[];
}

export interface Address {
  street: string;
  unit: string;
  postalCode: string;
}

export interface LegalEntity {
  accountName: string;
  country: string;
  companyRegNumber: string;
  taxNumber: string;
  invoicingAddress: Address;
  /** Salesforce IU record ID — present when populated from brand default IU */
  salesforceId?: string;
}

export interface BankAccount {
  name: string;
  holderName: string;
  contactEmail: string;
  billingCurrency: string;
  billingAddress?: Address;
  billingSameAsInvoicing: boolean;
  mailingAddress?: Address | null;
  mailingSameAsBilling: boolean;
}

export interface UploadFile {
  uid: string;
  name: string;
  status?: string;
  url?: string;
  /** S3 file key from presigned upload — used in API payload */
  fileKey?: string;
}

export interface AdSalesWizardData {
  brandId: string;
  cardDates: Record<string, Date>;
  selected: AdSalesInstance[];
  // Invoicing — brand-scoped
  legalEntity: LegalEntity | null;
  bankAccount: BankAccount | null;
  // 'brand_default' = pre-filled from the brand's saved details and untouched
  // by the merchant. 'manual' = merchant explicitly overrode (the legal
  // entity + bank account are treated as one decision — both fields flip
  // together via a single toggle in the step). The intent signal is the
  // override click, not the byte diff. Sent silently to BD so Salesforce
  // onboarding picks up new details for extra review.
  invoicingSource: 'brand_default' | 'manual';
  /**
   * Salesforce IU record ID resolved for the selected brand.
   * undefined = not yet fetched; null = fetched but brand has no linked IU;
   * string = IU found and its SF ID stored here.
   * When non-null, attached to payment.invoicingUnit on submit to trigger
   * nav_customer_number / issuing_entity enrichment on the backend.
   */
  iuSalesforceId?: string | null;
  // Transactional
  billingType: 'Direct' | 'Network';
  poNumber: string;
  merchantComment: string;
  networkProof: UploadFile[];
  // T&C
  acceptedTerms: boolean;
  // IO signing
  ioDownloadLink?: string;
  ioDownloaded: boolean;
  ioSignedAck: boolean;
  signedIO: UploadFile[];
}

// --- Constants ---

export const AD_GST_BY_COUNTRY: Record<string, number> = {
  SG: 0.09,
  MY: 0.06,
  TW: 0.05,
  AU: 0.1,
  PH: 0.12,
  TH: 0.07,
  ID: 0.11,
  VN: 0.08,
};

export const COUNTRY_NAME: Record<string, string> = {
  SG: 'Singapore',
  MY: 'Malaysia',
  AU: 'Australia',
  TW: 'Taiwan',
  ID: 'Indonesia',
  TH: 'Thailand',
  PH: 'Philippines',
  VN: 'Vietnam',
};

export const LEAD_TIME_DAYS = 5;

export const AD_STEPS = [
  { key: 'browse', title: 'Select packages & dates' },
  { key: 'invoicing', title: 'Invoicing details' },
  { key: 'review', title: 'Review' },
  { key: 'sign-io', title: 'Sign IO' },
];

// --- Ad Sales taxonomy ---
// Source of truth: `.claude/handoff/ui/SBData.jsx` (`AD_SALES_SUBTYPES`, `AD_CATEGORIES`).
// Synced 2026-05-30 to match the prototype's picker options + visual tagging.

export interface AdSalesSubtype {
  key: 'CATEGORY_DOMINATION' | 'BRAND_SUPER_WEEK' | 'PLATINUM_BOOSTER' | 'FLASH_SALE_BOOSTER' | 'CUSTOM';
  label: string;
  description: string;
}

export const AD_SALES_SUBTYPES: AdSalesSubtype[] = [
  {
    key: 'CATEGORY_DOMINATION',
    label: 'Category Domination',
    description: 'Own an entire category page across placements.',
  },
  { key: 'BRAND_SUPER_WEEK', label: 'Brand Super Week', description: 'Week-long multi-channel brand takeover.' },
  { key: 'PLATINUM_BOOSTER', label: 'Platinum Booster', description: 'Premium homepage + EDM + push bundle.' },
  {
    key: 'FLASH_SALE_BOOSTER',
    label: 'Flash Sale Booster',
    description: 'Short-burst paid amplification for a sale event.',
  },
  { key: 'CUSTOM', label: 'Custom Package', description: 'Bespoke configuration assembled with your AM.' },
];

export interface AdCategory {
  id: 'HOMEPAGE' | 'CATEGORY_PAGES' | 'DEALS_PROMOTIONS' | 'INBOX_ALERTS';
  label: string;
}

export const AD_CATEGORIES: AdCategory[] = [
  { id: 'HOMEPAGE', label: 'Homepage' },
  { id: 'CATEGORY_PAGES', label: 'Category pages' },
  { id: 'DEALS_PROMOTIONS', label: 'Deals & promotions' },
  { id: 'INBOX_ALERTS', label: 'Inbox & alerts' },
];

export const TERMS_AND_CONDITIONS = [
  '1. Parties. This Agreement for the purchase of advertising placements shall be between ShopBack and the Merchant named above.',
  '2. Fees. The Merchant shall pay ShopBack in accordance with the Payment Schedule. Where ShopBack issues an invoice, payment is due within 30 days. Late payment interest accrues at 3% per annum.',
  '3. Term. Services run from the Start Date to the End Date as stated.',
  "4. Termination. ShopBack may terminate this Agreement upon breach by the Merchant, or for convenience with 2 days' notice and pro-rata refund. The Merchant may not terminate once live.",
  '5. Cancellation. Cancellation charges and compensation for work, loss and damage may apply.',
  '6. Intellectual Property. The Merchant permits ShopBack to use its name, product information, logo and trademarks for the purpose of providing the agreed services.',
  '7. Advertisement and Artwork. The Merchant is responsible for providing artwork 10 days before the campaign start date.',
  '8. Taxes. The Merchant is responsible for all applicable GST/VAT/SST.',
];

// --- Mock Ad Sales Packages (Australia catalog) ---

export const AD_PACKAGES: AdSalesPackage[] = [
  {
    id: 'category-domination',
    name: 'Category Domination',
    description: 'Sitewide hero placements across app and web for one full month — the highest-volume reach package.',
    priceSGD: 52000,
    durationDays: 30,
    durationLabel: '30 days',
    slotsAvailable: 3,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          {
            id: 'a1',
            name: 'App Category Screen Hero banner',
            duration: '× 30 days',
            qty: 30,
            mode: 'Auto',
            group: 'AUTO',
          },
          {
            id: 'a2',
            name: 'App Search Autocomplete Banner',
            duration: '× 30 days',
            qty: 30,
            mode: 'Auto',
            group: 'AUTO',
          },
          {
            id: 'a3',
            name: 'App Campaign Screen Marketing Asset 1',
            duration: '× 7 days',
            qty: 7,
            mode: 'Auto',
            group: 'AUTO',
          },
        ],
      },
      {
        label: 'On ShopBack web',
        assets: [
          {
            id: 'a4',
            name: 'Web Category Screen Hero banner',
            duration: '× 30 days',
            qty: 30,
            mode: 'Auto',
            group: 'AUTO',
          },
          { id: 'a5', name: 'Web Homepage Spotlight 1', duration: '× 7 days', qty: 7, mode: 'Auto', group: 'AUTO' },
        ],
      },
      {
        label: 'Outbound',
        assets: [
          { id: 'a6', name: 'Promotional Email — tile', duration: '× 5 sends', qty: 5, mode: 'Manual', group: 'EDM' },
          { id: 'a7', name: 'Push Notification — solus', duration: '× 1 send', qty: 1, mode: 'Manual', group: 'CRM' },
        ],
      },
    ],
  },
  {
    id: 'brand-takeover',
    name: 'Brand Takeover',
    description: 'Full-funnel awareness — homepage hero, app spotlight, and dedicated email push for one month.',
    priceSGD: 32000,
    durationDays: 30,
    durationLabel: '30 days',
    slotsAvailable: 5,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          { id: 'b1', name: 'App UHS Masthead', duration: '× 30 days', qty: 30, mode: 'Auto', group: 'AUTO' },
          {
            id: 'b2',
            name: 'App Category Screen Hero banner',
            duration: '× 30 days',
            qty: 30,
            mode: 'Auto',
            group: 'AUTO',
          },
          {
            id: 'b3',
            name: 'App Search History Banner — All tab',
            duration: '× 30 days',
            qty: 30,
            mode: 'Auto',
            group: 'AUTO',
          },
        ],
      },
      {
        label: 'On ShopBack web',
        assets: [
          { id: 'b4', name: 'Web Homepage Hero banner', duration: '× 30 days', qty: 30, mode: 'Auto', group: 'AUTO' },
          {
            id: 'b5',
            name: 'Web Category Screen Hero banner',
            duration: '× 30 days',
            qty: 30,
            mode: 'Auto',
            group: 'AUTO',
          },
        ],
      },
      {
        label: 'Outbound',
        assets: [
          { id: 'b6', name: 'Promotional Email — tile', duration: '× 3 sends', qty: 3, mode: 'Manual', group: 'EDM' },
          { id: 'b7', name: 'Push Notification — solus', duration: '× 1 send', qty: 1, mode: 'Manual', group: 'CRM' },
        ],
      },
    ],
  },
  {
    id: 'shopfest-app-takeover',
    name: 'ShopFest App Homepage Takeover',
    description: 'App-first takeover during the ShopFest mega-sale event — captures peak-traffic shoppers.',
    priceSGD: 20000,
    durationDays: 1,
    durationLabel: 'ShopFest event',
    slotsAvailable: 1,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          { id: 'sf1', name: 'App - UHS - OA - Spotlight 2', duration: '× event', qty: 1, mode: 'Auto', group: 'AUTO' },
          {
            id: 'sf2',
            name: 'App Search History Banner — All tab',
            duration: '× event',
            qty: 1,
            mode: 'Auto',
            group: 'AUTO',
          },
          {
            id: 'sf3',
            name: 'ShopFest Home Screen Brand Spotlight',
            duration: '× event',
            qty: 1,
            mode: 'Auto',
            group: 'AUTO',
          },
          {
            id: 'sf4',
            name: 'ShopFest 15" Native Video or GIF',
            duration: '× event',
            qty: 1,
            mode: 'Manual',
            group: 'MANUAL',
          },
        ],
      },
      {
        label: 'On ShopBack web',
        assets: [
          { id: 'sf5', name: 'Web Homepage Hero banner', duration: '× event', qty: 1, mode: 'Auto', group: 'AUTO' },
        ],
      },
    ],
  },
  {
    id: 'brand-super-week',
    name: 'Brand Super Week',
    description: 'Week-long premium placement plus a funded reward challenge to drive new shopper acquisition.',
    priceSGD: 20000,
    durationDays: 7,
    durationLabel: '7 days',
    slotsAvailable: 2,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          {
            id: 'sw1',
            name: 'App - UHS - OA - Spotlight 3',
            duration: '× 7 days',
            qty: 7,
            mode: 'Auto',
            group: 'AUTO',
          },
        ],
      },
      {
        label: 'On ShopBack web',
        assets: [
          { id: 'sw2', name: 'Web Homepage Hero banner', duration: '× 7 days', qty: 7, mode: 'Auto', group: 'AUTO' },
        ],
      },
      {
        label: 'Outbound',
        assets: [
          { id: 'sw3', name: 'Promotional Email - solus', duration: '× 1 send', qty: 1, mode: 'Manual', group: 'EDM' },
          { id: 'sw4', name: 'Push Notification - solus', duration: '× 1 send', qty: 1, mode: 'Manual', group: 'CRM' },
        ],
      },
    ],
  },
  {
    id: 'brand-boost',
    name: 'Brand Boost',
    description:
      'Bottom-of-funnel push with app spotlight, pop-up, and dedicated notification — best for re-engagement.',
    priceSGD: 20000,
    durationDays: 7,
    durationLabel: '7 days',
    slotsAvailable: 2,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          {
            id: 'bb1',
            name: 'App - UHS - OA - Spotlight 3',
            duration: '× 7 days',
            qty: 7,
            mode: 'Auto',
            group: 'AUTO',
          },
          { id: 'bb2', name: 'Pop up', duration: '× 1 day', qty: 1, mode: 'Manual', group: 'MANUAL' },
        ],
      },
      {
        label: 'Outbound',
        assets: [
          { id: 'bb3', name: 'Push Notification - solus', duration: '× 1 send', qty: 1, mode: 'Manual', group: 'CRM' },
        ],
      },
    ],
  },
  {
    id: 'hero-booster',
    name: 'Hero Booster',
    description: 'Homepage hero on app and web for 7 days, plus a solus push notification.',
    priceSGD: 16000,
    durationDays: 7,
    durationLabel: '7 days',
    slotsAvailable: 3,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          {
            id: 'hb1',
            name: 'App - UHS - OA - Spotlight 3',
            duration: '× 7 days',
            qty: 7,
            mode: 'Auto',
            group: 'AUTO',
          },
        ],
      },
      {
        label: 'On ShopBack web',
        assets: [
          { id: 'hb2', name: 'Web Homepage Hero banner', duration: '× 7 days', qty: 7, mode: 'Auto', group: 'AUTO' },
        ],
      },
      {
        label: 'Outbound',
        assets: [
          { id: 'hb3', name: 'Push Notification - solus', duration: '× 1 send', qty: 1, mode: 'Manual', group: 'CRM' },
        ],
      },
    ],
  },
  {
    id: 'platinum-booster',
    name: 'Platinum Booster',
    description: 'Commission uplift only — no advertising placements bundled with this package.',
    priceSGD: 8500,
    durationDays: 30,
    durationLabel: '30 days',
    slotsAvailable: 8,
    surfaces: [],
  },
  {
    id: 'top-picks-feature',
    name: 'Top Picks Feature',
    description: 'Lighter-touch brand-mention spotlight across editorial, social and outbound for 3 days.',
    priceSGD: 7000,
    durationDays: 3,
    durationLabel: '3 days',
    slotsAvailable: 15,
    surfaces: [
      {
        label: 'On ShopBack app',
        assets: [
          { id: 'e1', name: 'App - UHS - OA - Spotlight 3', duration: '× 3 days', qty: 3, mode: 'Auto', group: 'AUTO' },
        ],
      },
      {
        label: 'On ShopBack web',
        assets: [
          { id: 'e2', name: 'Web Homepage Hero banner', duration: '× 3 days', qty: 3, mode: 'Auto', group: 'AUTO' },
        ],
      },
      {
        label: 'Outbound',
        assets: [
          { id: 'e3', name: 'FB/IG Story - dedicated', duration: '× 1 count', qty: 1, mode: 'Manual', group: 'MANUAL' },
          { id: 'e4', name: 'Promotional Email - solus', duration: '× 1 count', qty: 1, mode: 'Manual', group: 'EDM' },
          {
            id: 'e5',
            name: 'Push Notification - brand mention',
            duration: '× 1 count',
            qty: 1,
            mode: 'Manual',
            group: 'CRM',
          },
        ],
      },
    ],
  },
  {
    id: 'gold-booster',
    name: 'Gold Booster',
    description: 'Entry-level rate uplift commitment — no ad placements included.',
    priceSGD: 750,
    durationDays: 30,
    durationLabel: '30 days',
    slotsAvailable: 20,
    surfaces: [],
  },
];

// --- Blocked dates (per package) ---
// Mock per-package inventory data for the AvailabilityDatePicker dot painter.
// Same model as Upsize `BLOCKED_DATES_BY_PACKAGE`: keyed by AD_PACKAGES.id,
// each entry is an array of YYYY-MM-DD strings the merchant should see as
// "heavily booked" (red dot). Per design 2026-05-30 the dots are purely
// visual — the date stays selectable; ShopBack reviews every submission.

export const BLOCKED_DATES_BY_AD_PACKAGE: Record<string, string[]> = {
  'category-domination': ['2026-05-11', '2026-05-12', '2026-05-13', '2026-08-09', '2026-08-10'],
  'brand-super-week': [
    '2026-05-09',
    '2026-05-10',
    '2026-05-11',
    '2026-05-12',
    '2026-06-12',
    '2026-06-13',
    '2026-08-09',
    '2026-08-10',
  ],
  'platinum-booster': ['2026-05-08', '2026-05-09', '2026-05-10', '2026-08-09', '2026-11-11'],
};

/**
 * Per-cell availability state for AdSales date pickers.
 * Returns `null` for past dates so the picker doesn't paint dots on cells
 * that are already disabled (per design 2026-05-30 — past dates don't need
 * availability indication, they're not selectable anyway).
 */
export const availabilityFor = (
  packageId: string | null | undefined,
  date: Date | moment.Moment
): 'healthy' | 'sold-out' | null => {
  if (!packageId) return null;
  const m = moment.isMoment(date) ? date : moment(date);
  if (m.isBefore(moment().startOf('day'))) return null;
  const list = BLOCKED_DATES_BY_AD_PACKAGE[packageId];
  if (!list) return 'healthy';
  return list.includes(m.format('YYYY-MM-DD')) ? 'sold-out' : 'healthy';
};

// --- Helpers ---

export const fmtPrice = (n: number, currency = 'AUD'): string => `${currency} ${(n || 0).toLocaleString('en-AU')}`;

export const fmtDate = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const computeEnd = (start: Date | null, days: number): Date | null => {
  if (!start || !days) return null;
  const e = new Date(start);
  e.setDate(e.getDate() + days - 1);
  return e;
};

export const todayPlus = (n: number): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return d;
};

export const adGstFor = (country: string): number => AD_GST_BY_COUNTRY[country] || 0.09;

export const pkgPrice = (p: AdSalesPackage): number => p.priceSGD || 0;

export const durationFor = (pkg: AdSalesPackage | undefined) => ({
  days: pkg?.durationDays || 7,
  label: pkg?.durationLabel || '1 week',
});

/** Flatten all assets from surfaces into a single sorted list */
export const flattenAssets = (pkg: AdSalesPackage | undefined): AdSalesAsset[] => {
  if (!pkg) return [];
  const rows: AdSalesAsset[] = [];
  (pkg.surfaces || []).forEach((s) => (s.assets || []).forEach((a) => rows.push(a)));
  return rows.sort((a, b) => (b.qty || 1) - (a.qty || 1));
};

/** Longest asset qty in a package — defines the booking window length */
export const maxAssetDuration = (pkg: AdSalesPackage | undefined): number => {
  let max = 1;
  flattenAssets(pkg).forEach((a) => {
    const q = a.qty || 1;
    if (q > max) max = q;
  });
  return max;
};

/** True if this asset spans the entire booking window */
export const isFullWindowAsset = (asset: AdSalesAsset, pkg: AdSalesPackage): boolean =>
  (asset.qty || 1) >= maxAssetDuration(pkg);

/**
 * Returns an array of `count` consecutive Date objects starting from `startDate`
 * (UTC day boundaries). Used to seed the default schedule for schedulable assets.
 */
export function defaultAssetDays(startDate: Date | null, count: number): Date[] {
  if (!startDate || count <= 0) return [];
  const days: Date[] = [];
  // Extract the local calendar date string (same as toUtcDayStart in wizardMappers.ts) so
  // the anchor is UTC midnight of the user-selected calendar day, not UTC midnight of the
  // UTC date. setUTCHours(0,0,0,0) on a timezone-aware Date object shifts the anchor one
  // calendar day earlier for east-of-UTC browsers (e.g. SGT/UTC+8: July 11 local
  // = July 10 16:00 UTC → floors to July 10 00:00 UTC instead of July 11 00:00 UTC).
  const baseMs = moment.utc(moment(startDate).format('YYYY-MM-DD'), 'YYYY-MM-DD').valueOf();
  for (let i = 0; i < count; i++) {
    days.push(new Date(baseMs + i * 86_400_000));
  }
  return days;
}

/** Format asset qty — "30 days" / "5 sends" */
export const fmtAssetQty = (asset: AdSalesAsset): string => {
  const qty = Math.max(1, asset.qty || 1);
  const dur = (asset.duration || '').toLowerCase();
  const last = dur.replace(/^×\s*/, '').trim().split(/\s+/).pop() || '';
  const isDays = ['day', 'days', 'event'].includes(last);
  if (isDays) return `${qty} ${qty === 1 ? 'day' : 'days'}`;
  return `${qty} ${qty === 1 ? 'count' : 'counts'}`;
};

export const computeInstanceEnd = (inst: AdSalesInstance, pkg: AdSalesPackage | undefined): Date | null => {
  if (!inst?.startDate || !pkg) return null;
  const days = flattenAssets(pkg).length > 0 ? maxAssetDuration(pkg) : pkg.durationDays;
  return computeEnd(inst.startDate, days);
};

export const fmtInstanceRange = (inst: AdSalesInstance, pkg: AdSalesPackage | undefined): string => {
  const s = inst.startDate;
  const e = computeInstanceEnd(inst, pkg);
  if (!s || !e) return '';
  if (fmtDate(s) === fmtDate(e)) return fmtDate(s);
  return `${fmtDate(s)} – ${fmtDate(e)}`;
};

/** Check if all schedulable assets in an instance have their dates picked */
export const instanceFullyScheduled = (inst: AdSalesInstance, pkg: AdSalesPackage | undefined): boolean => {
  if (!inst || !inst.startDate) return false;
  // Server-provided assets are authoritative: their names match assetSchedule keys and
  // carry timeType from the API. Prefer them over the catalog even when pkg is resolved,
  // because the catalog's asset names may be UUIDs when assetDetails haven't loaded.
  if (inst.assets && inst.assets.length > 0) {
    return inst.assets.every((a) => {
      if (a.isAutomated || a.group === 'AUTO') return true;
      // Full-window assets span the entire booking window — they don't
      // need per-day picks. `AdSalesSetupStep.addPackage` intentionally
      // skips seeding `assetSchedule` for them, so without this guard
      // every package whose longest asset is full-window would block
      // Continue forever. Mirrors the same guard on the catalog
      // fallback branch below.
      if (pkg && isFullWindowAsset(a, pkg)) return true;
      const days = inst.assetSchedule?.[a.name] || [];
      return days.length >= 1;
    });
  }
  if (!pkg) return true;
  return flattenAssets(pkg).every((a) => {
    if (isFullWindowAsset(a, pkg)) return true;
    const days = inst.assetSchedule?.[a.name] || [];
    return days.length === Math.max(1, a.qty || 1);
  });
};

/**
 * Label for the tax AMOUNT row, e.g. "GST" / "VAT". Distinct from `taxLabelForCountry`, which names
 * the merchant's tax REGISTRATION NUMBER. The two sit a few rows apart on the booking summary, so
 * reusing one for the other charged the merchant for a registration number (MIC-3861).
 */
export const taxAmountLabelForCountry = (country: string): string =>
  taxLabelForCountry(country).replace(/\s*Number$/, '');

export const taxLabelForCountry = (country: string): string => {
  if (['AU', 'SG', 'MY', 'ID'].includes(country)) return 'GST Number';
  if (['DE', 'FR', 'NL', 'IT', 'ES', 'GB', 'IE'].includes(country)) return 'VAT Number';
  return 'Tax Number';
};

export const currenciesForCountry = (country: string): string[] => {
  const map: Record<string, string[]> = {
    SG: ['SGD', 'USD'],
    MY: ['MYR', 'USD'],
    AU: ['AUD', 'USD'],
    TW: ['TWD', 'USD'],
    ID: ['IDR', 'USD'],
    TH: ['THB', 'USD'],
    PH: ['PHP', 'USD'],
    VN: ['VND', 'USD'],
  };
  return map[country] || ['USD'];
};

/**
 * Map a resolved ApiInvoicingUnit from the BE into the wizard's LegalEntity + BankAccount shapes.
 * Consumed by CreateAdSales when the IU fetch returns a result for the selected brand.
 */
export function iuToInvoicingDefaults(iu: ApiInvoicingUnit): { legalEntity: LegalEntity; bankAccount: BankAccount } {
  const billingAddr: Address | undefined = iu.billingAddress
    ? {
        street: iu.billingAddress.street || '',
        unit: iu.billingAddress.unit || '',
        postalCode: iu.billingAddress.postalCode || '',
      }
    : iu.billingAddressString
      ? { street: iu.billingAddressString, unit: '', postalCode: '' }
      : undefined;

  const mailingAddr: Address | undefined =
    iu.billingToMailing || (!iu.mailingAddress && !iu.mailingAddressString)
      ? undefined
      : iu.mailingAddress
        ? {
            street: iu.mailingAddress.street || '',
            unit: iu.mailingAddress.unit || '',
            postalCode: iu.mailingAddress.postalCode || '',
          }
        : iu.mailingAddressString
          ? { street: iu.mailingAddressString, unit: '', postalCode: '' }
          : undefined;

  return {
    legalEntity: {
      salesforceId: iu.salesforceId,
      accountName: iu.name,
      country: iu.country,
      companyRegNumber: iu.companyRegistrationNumber || '',
      taxNumber: iu.gstNumber || '',
      invoicingAddress: billingAddr ?? { street: '', unit: '', postalCode: '' },
    },
    bankAccount: {
      name: iu.accountHolderName || iu.name,
      holderName: iu.accountHolderName || iu.name,
      contactEmail: iu.billingEmail || '',
      billingCurrency: iu.currency || '',
      billingAddress: billingAddr,
      billingSameAsInvoicing: true,
      mailingAddress: mailingAddr ?? null,
      mailingSameAsBilling: iu.billingToMailing !== false && !mailingAddr,
    },
  };
}

export const newInstanceId = (): string => `ins_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const makeInitialData = (): AdSalesWizardData => ({
  brandId: '',
  cardDates: {},
  selected: [],
  legalEntity: null,
  bankAccount: null,
  // Flips to 'manual' on brand-select if the brand has no linked IU.
  invoicingSource: 'brand_default',
  iuSalesforceId: undefined,
  billingType: 'Direct',
  poNumber: '',
  merchantComment: '',
  networkProof: [],
  acceptedTerms: false,
  ioDownloaded: false,
  ioSignedAck: false,
  signedIO: [],
});
